// L'EFFACEUR — TimelineEvents
// CCC.9 : timeline des derniers événements de toutes les missions du user.
// Inspiré du suivi Colissimo : "12/04 Mandat signé · 13/04 Lettre envoyée à EDF
// · 18/04 EDF a confirmé". Donne un sentiment de progression et de maîtrise.
//
// Source de données : status_history présent dans chaque mission. On agrège,
// trie par date desc, prend les 5 derniers.

import { motion } from 'framer-motion';
import { Activity, CheckCircle, Mail, FileSignature, Send, AlertCircle, Package, Clock } from 'lucide-react';

const STATUS_ICONS = {
  default: Activity,
  cree: Package,
  nouveau: Package,
  signed: FileSignature,
  signe: FileSignature,
  mandat_signe: FileSignature,
  envoi: Send,
  envoye: Send,
  dispatch: Send,
  email_envoye: Mail,
  recu: CheckCircle,
  confirme: CheckCircle,
  termine: CheckCircle,
  efface: CheckCircle,
  cloture: CheckCircle,
  attente: Clock,
  bloque: AlertCircle,
  erreur: AlertCircle,
};

const STATUS_COLORS = {
  cree: 'text-gray-400',
  nouveau: 'text-cyan-400',
  signe: 'text-cyan-400',
  signed: 'text-cyan-400',
  mandat_signe: 'text-cyan-400',
  envoi: 'text-orange-400',
  envoye: 'text-orange-400',
  dispatch: 'text-orange-400',
  email_envoye: 'text-orange-400',
  recu: 'text-success',
  confirme: 'text-success',
  termine: 'text-success',
  efface: 'text-success',
  cloture: 'text-success',
  attente: 'text-yellow-400',
  bloque: 'text-red-400',
  erreur: 'text-red-400',
  default: 'text-gray-400',
};

const PACK_LABELS = {
  mission_demenagement: 'Déménagement',
  nouveau_depart: 'Déménagement',
  mission_heritage: 'Héritage',
  serenite_succession: 'Héritage',
  mission_succession: 'Héritage',
  mission_audit: 'Audit',
  audit_pouvoir_achat: 'Audit',
};

// Helper : "il y a X temps"
const relativeTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `il y a ${diffH}h`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `il y a ${diffD}j`;
  if (diffD < 30) return `il y a ${Math.round(diffD / 7)}sem`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const formatLabel = (status) => {
  if (!status) return 'Mise à jour';
  const map = {
    cree: 'Mission créée',
    nouveau: 'Mission créée',
    signe: 'Mandat signé',
    signed: 'Mandat signé',
    mandat_signe: 'Mandat signé',
    envoi: 'Courriers envoyés',
    envoye: 'Courriers envoyés',
    dispatch: 'Démarches lancées',
    email_envoye: 'Email envoyé à organisme',
    recu: 'Réponse reçue',
    confirme: 'Résiliation confirmée',
    termine: 'Mission terminée',
    efface: 'Données effacées',
    cloture: 'Mission clôturée',
    attente: 'En attente',
    bloque: 'Action requise',
    erreur: 'Incident détecté',
  };
  return map[status.toLowerCase()] || status.replace(/_/g, ' ');
};

// Aggregate events from all missions
const aggregateEvents = (missions, limit = 5) => {
  const events = [];
  for (const m of missions) {
    const packLabel = PACK_LABELS[m.pack_type] || 'Mission';
    const history = m.status_history || [];
    for (const h of history) {
      if (!h?.changed_at) continue;
      events.push({
        ts: h.changed_at,
        status: (h.to || '').toLowerCase(),
        from: (h.from || '').toLowerCase(),
        note: h.note,
        pack: packLabel,
      });
    }
    // Si pas d'history mais une création, ajoute un faux event
    if (history.length === 0 && (m.created_at || m.updated_at)) {
      events.push({
        ts: m.created_at || m.updated_at,
        status: (m.admin_status || m.status || 'nouveau').toLowerCase(),
        pack: packLabel,
      });
    }
  }
  events.sort((a, b) => new Date(b.ts) - new Date(a.ts));
  return events.slice(0, limit);
};

const TimelineEvents = ({ missions = [] }) => {
  const events = aggregateEvents(missions, 5);

  if (events.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.16 }}
      data-testid="timeline-events"
      className="relative overflow-hidden rounded-xl border-2 border-magenta-400/30 bg-magenta-400/5 p-5"
    >
      {/* CCC.13 — Style cyber/scan-laser cohérent avec NextAction et SavingsCounter */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,0,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,0,255,0.5) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
      <div
        className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-magenta-400 to-transparent opacity-60 pointer-events-none"
        style={{ animation: 'timeline-scan 5s linear infinite' }}
      />
      <style>{`
        @keyframes timeline-scan {
          0% { transform: translateY(0); opacity: 0.4; }
          50% { transform: translateY(140px); opacity: 0.9; }
          100% { transform: translateY(0); opacity: 0.4; }
        }
      `}</style>
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-magenta-400 text-xs font-mono uppercase mb-0.5 tracking-[0.2em]">// ACTIVITÉ_RÉCENTE</p>
            <h3 className="font-orbitron text-base font-bold text-white">Les 5 derniers événements</h3>
          </div>
          <div className="w-10 h-10 rounded-lg bg-magenta-400/15 border border-magenta-400/30 flex items-center justify-center relative">
            <div className="absolute inset-0 bg-magenta-400/20 blur-xl rounded-lg" />
            <Activity className="w-5 h-5 text-magenta-400 relative" />
          </div>
        </div>

        <ol className="relative border-l border-gray-800 ml-2 space-y-3">
          {events.map((e, idx) => {
            const Icon = STATUS_ICONS[e.status] || STATUS_ICONS.default;
            const color = STATUS_COLORS[e.status] || STATUS_COLORS.default;
            return (
              <li key={idx} className="ml-4 relative">
                <div className={`absolute -left-[27px] top-0.5 w-4 h-4 rounded-full bg-void border-2 ${color.replace('text-', 'border-')} flex items-center justify-center`}>
                  <Icon className={`w-2.5 h-2.5 ${color}`} />
                </div>
                <div className="pb-1">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <p className="text-white text-sm font-medium">{formatLabel(e.status)}</p>
                    <span className="text-[10px] font-mono text-gray-500 flex-shrink-0">{relativeTime(e.ts)}</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Mission <span className="text-gray-400">{e.pack}</span>
                    {e.note && <span> · {e.note}</span>}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </motion.div>
  );
};

export default TimelineEvents;
